import { CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import NetworkStatusNotification from "components/NetworkStatus/NetworkStatusNotification";
import MainLayoutContext from "contexts/MainLayoutContext";
import { RoomUpdateProvider } from "contexts/RoomUpdateContext";
import { WebSocketProvider } from "contexts/WebSocketContext";
import { useGlobalSmartSummaryListener } from "hooks/usePatientDataRefresh";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import Router from "routes/Router";
import { getSentry, captureException } from "services/monitoring";
import { sessionManager } from "utils/sessionManager";
import { store } from "./redux/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors for faster session expiry handling
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Default retry logic for other errors
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors for faster session expiry handling
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Default retry logic for other errors
        return failureCount < 3;
      },
    },
  },
});

queryClient.setMutationDefaults(["mutation"], {
  onError: (error: any) => {
    captureException(error, { source: "react-query", type: "mutation" });
  },
});

// Component that uses WebSocket hooks (must be inside WebSocketProvider)
function GlobalWebSocketListener() {
  useGlobalSmartSummaryListener();
  return null;
}

interface AppProps {
  initialFeatureFlags?: Record<string, boolean>;
}

function App({ initialFeatureFlags }: AppProps) {
  const [title, setTitle] = useState("");
  const [titleBody, setTitleBody] = useState("");
  // for tanstack chrome debugger (dev only)
  if (process.env.NODE_ENV === "development") {
    (window as any).__TANSTACK_QUERY_CLIENT__ = queryClient;
  }
  const dispatch = useDispatch();

  // Initialize session manager with store
  useEffect(() => {
    sessionManager.init(store);
  }, []);

  // Force landscape display on portrait devices narrower than 1280px.
  // Uses resize event (reliable in DevTools) instead of a CSS media query.
  useEffect(() => {
    const applyPortraitLock = () => {
      const isPortrait = window.innerHeight > window.innerWidth;
      const isNarrow = window.innerWidth < 1280;
      document.documentElement.classList.toggle("portrait-lock", isPortrait && isNarrow);
    };
    applyPortraitLock();
    window.addEventListener("resize", applyPortraitLock);
    window.addEventListener("orientationchange", applyPortraitLock);
    return () => {
      window.removeEventListener("resize", applyPortraitLock);
      window.removeEventListener("orientationchange", applyPortraitLock);
      document.documentElement.classList.remove("portrait-lock");
    };
  }, []);

  useEffect(() => {
    // Instead of making API call, dispatch the data we already have from Context
    if (initialFeatureFlags) {
      dispatch({
        type: "getFlags/getFlagsSuccess",
        payload: initialFeatureFlags,
      });
    }
  }, [dispatch, initialFeatureFlags]);

  return (
    <MainLayoutContext.Provider
      value={{
        title: title,
        setTitle: setTitle,
        titleBody: titleBody,
        setTitleBody: setTitleBody,
      }}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <WebSocketProvider>
            <RoomUpdateProvider>
              <GlobalWebSocketListener />
              <CssBaseline />
              <NetworkStatusNotification />
              <Router />
            </RoomUpdateProvider>
          </WebSocketProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </MainLayoutContext.Provider>
  );
}

// Conditionally wrap with Sentry profiler if available
const Sentry = getSentry();
export default Sentry?.withProfiler ? Sentry.withProfiler(App) : App;
